import type { CodecResult, ParsedMessage, ParsedMessageEntity } from "../types"
import {
  attempt,
  checkedSerialization,
  dateEntity,
  entityTree,
  linkEntity,
  ParsedBuilder,
  reject,
  representability,
  type EntityNode,
} from "./shared"

const tagTypes: Record<string, string> = {
  b: "bold",
  strong: "bold",
  i: "italic",
  em: "italic",
  u: "underline",
  ins: "underline",
  s: "strikethrough",
  strike: "strikethrough",
  del: "strikethrough",
  "tg-spoiler": "spoiler",
  span: "spoiler",
  code: "code",
  pre: "pre",
  a: "text_link",
  "tg-emoji": "custom_emoji",
  "tg-time": "date_time",
  blockquote: "blockquote",
}
const references: Record<string, string> = {
  lt: "<",
  gt: ">",
  amp: "&",
  quot: '"',
}

function reference(
  source: string,
  offset: number,
): { value: string; length: number } {
  const end = source.indexOf(";", offset + 1)
  if (end === -1)
    reject("INVALID_HTML_REFERENCE", "Unterminated HTML reference", offset)
  const name = source.slice(offset + 1, end)
  if (Object.hasOwn(references, name))
    return { value: references[name], length: end + 1 - offset }
  if (/^#(?:[0-9]+|x[0-9a-f]+)$/i.test(name)) {
    const numeric =
      name[1].toLowerCase() === "x"
        ? parseInt(name.slice(2), 16)
        : Number(name.slice(1))
    if (
      numeric > 0 &&
      numeric <= 0x10ffff &&
      !(numeric >= 0xd800 && numeric <= 0xdfff)
    )
      return { value: String.fromCodePoint(numeric), length: end + 1 - offset }
  }
  return reject(
    "INVALID_HTML_REFERENCE",
    "Unsupported or invalid HTML reference",
    offset,
    end + 1 - offset,
  )
}

interface Attribute {
  name: string
  value: string
  offset: number
  length: number
}
interface Tag {
  name: string
  closing: boolean
  attributes: Attribute[]
  end: number
}

/** Lexes tags before any semantic interpretation. No DOM is created, repaired,
 * executed or fetched. Quoted values are scanned independently of tag syntax. */
function readTag(source: string, start: number): Tag {
  let i = start + 1
  const closing = source[i] === "/"
  if (closing) i++
  const nameStart = i
  while (/[a-z0-9-]/i.test(source[i] ?? "") && i < source.length) i++
  if (i === nameStart)
    reject("INVALID_HTML_TAG", "Expected a Telegram tag", start)
  const name = source.slice(nameStart, i).toLowerCase()
  const attributes: Attribute[] = []
  while (i < source.length) {
    const gapStart = i
    while (/\s/.test(source[i] ?? "") && i < source.length) i++
    if (source[i] === ">") return { name, closing, attributes, end: i + 1 }
    if (closing || source[i] === "/" || gapStart === i)
      reject("INVALID_HTML_TAG", "Invalid tag syntax", i)
    const attributeStart = i
    while (/[a-z0-9-]/i.test(source[i] ?? "") && i < source.length) i++
    if (attributeStart === i)
      reject(
        "INVALID_HTML_ATTRIBUTE",
        "Expected an attribute name",
        i,
        i === source.length ? 0 : 1,
      )
    const attributeName = source.slice(attributeStart, i).toLowerCase()
    if (attributes.some((attribute) => attribute.name === attributeName))
      reject(
        "DUPLICATE_HTML_ATTRIBUTE",
        "Duplicate attribute",
        attributeStart,
        i - attributeStart,
      )
    while (/\s/.test(source[i] ?? "") && i < source.length) i++
    let value = ""
    let offset = i
    let length = 0
    if (source[i] === "=") {
      i++
      while (/\s/.test(source[i] ?? "") && i < source.length) i++
      const quote = ['"', "'"].includes(source[i]) ? source[i++] : undefined
      offset = i
      while (
        i < source.length &&
        (quote ? source[i] !== quote : !/[\s>]/.test(source[i]))
      ) {
        if (source[i] === "<" || (!quote && /["'=]/.test(source[i])))
          reject("INVALID_HTML_ATTRIBUTE", "Invalid attribute value", i)
        if (source[i] === "&") {
          const decoded = reference(source, i)
          value += decoded.value
          i += decoded.length
        } else value += source[i++]
      }
      length = i - offset
      if (quote) {
        if (source[i] !== quote)
          reject(
            "UNCLOSED_HTML_ATTRIBUTE",
            "Unclosed quoted attribute",
            offset,
            length,
          )
        i++
      } else if (!length)
        reject(
          "INVALID_HTML_ATTRIBUTE",
          "Attribute needs a value",
          offset,
          offset === source.length ? 0 : 1,
        )
    }
    attributes.push({ name: attributeName, value, offset, length })
  }
  return reject(
    "UNCLOSED_HTML_TAG",
    "Unclosed tag",
    start,
    source.length - start,
  )
}

interface Frame {
  name: string
  start: number
  entity: ParsedMessageEntity
  codeInPre?: boolean
  codeEnd?: number
  destination?: Attribute
}

export function parseTelegramHtml(source: string): CodecResult<ParsedMessage> {
  return attempt(() => {
    const builder = new ParsedBuilder(source, {
      kind: "markup",
      parseMode: "HTML",
    })
    const stack: Frame[] = []
    let i = 0
    while (i < source.length) {
      if (source[i] !== "<") {
        if (source[i] === ">")
          reject("UNESCAPED_HTML_CHARACTER", "Escape > as &gt;", i)
        if (source[i] === "&") {
          const decoded = reference(source, i)
          builder.append(decoded.value, i, decoded.length)
          i += decoded.length
        } else {
          const char = String.fromCodePoint(source.codePointAt(i)!)
          builder.append(char, i, char.length)
          i += char.length
        }
        continue
      }
      const tag = readTag(source, i)
      if (!Object.hasOwn(tagTypes, tag.name))
        reject(
          "UNSUPPORTED_HTML_TAG",
          `Unsupported Telegram tag: ${tag.name}`,
          i,
          tag.end - i,
        )
      if (tag.closing) {
        const frame = stack.pop()
        if (!frame || frame.name !== tag.name)
          reject(
            "MISMATCHED_HTML_TAG",
            "Mismatched closing tag",
            i,
            tag.end - i,
          )
        if (frame.codeInPre)
          stack[stack.length - 1].codeEnd = builder.text.length
        if (
          frame.codeEnd !== undefined &&
          frame.codeEnd !== builder.text.length
        )
          reject(
            "INVALID_PRE_CODE",
            "Language code must wrap the complete pre content",
            frame.start,
            tag.end - frame.start,
          )
        frame.entity.length = builder.text.length - frame.entity.offset
        if (!frame.codeInPre && frame.entity.length === 0)
          builder.entities.splice(builder.entities.indexOf(frame.entity), 1)
        if (frame.destination)
          builder.destination(
            frame.destination.offset,
            frame.destination.length,
            frame.entity.offset,
            frame.entity.length,
          )
      } else {
        const parent = stack[stack.length - 1]
        const codeInPre = tag.name === "code" && parent?.name === "pre"
        const allowed: Record<string, string[]> = {
          a: ["href"],
          span: ["class"],
          code: codeInPre ? ["class"] : [],
          "tg-emoji": ["emoji-id"],
          "tg-time": ["unix", "format"],
          blockquote: ["expandable"],
        }
        for (const attribute of tag.attributes)
          if (!(allowed[tag.name] ?? []).includes(attribute.name))
            reject(
              "UNSUPPORTED_HTML_ATTRIBUTE",
              `Unsupported ${tag.name} attribute: ${attribute.name}`,
              attribute.offset,
              Math.max(attribute.length, 1),
            )
        const attrs = Object.fromEntries(
          tag.attributes.map((attribute) => [attribute.name, attribute]),
        )
        const offset = builder.text.length
        let entity: ParsedMessageEntity
        if (codeInPre) {
          if (
            offset !== parent.entity.offset ||
            parent.entity.type !== "pre" ||
            parent.entity.language !== undefined
          )
            reject(
              "INVALID_PRE_CODE",
              "Language code must wrap the complete pre content",
              i,
              tag.end - i,
            )
          const language = attrs.class?.value
          if (
            language !== undefined &&
            (!language.startsWith("language-") ||
              language.length === 9 ||
              /\s/.test(language))
          )
            reject(
              "INVALID_LANGUAGE",
              "Expected language-NAME class",
              i,
              tag.end - i,
            )
          if (language) parent.entity.language = language.slice(9)
          entity = parent.entity
        } else if (tag.name === "a") {
          if (!attrs.href)
            reject(
              "MISSING_HTML_ATTRIBUTE",
              "A link requires href",
              i,
              tag.end - i,
            )
          entity = linkEntity(
            attrs.href.value,
            offset,
            0,
            attrs.href.offset,
            attrs.href.length,
            source.slice(
              attrs.href.offset,
              attrs.href.offset + attrs.href.length,
            ),
          )
        } else if (tag.name === "tg-emoji") {
          if (
            !attrs["emoji-id"] ||
            !/^[1-9][0-9]*$/.test(attrs["emoji-id"].value)
          )
            reject(
              "INVALID_EMOJI_ID",
              "Expected decimal emoji-id",
              i,
              tag.end - i,
            )
          entity = {
            type: "custom_emoji",
            offset,
            length: 0,
            custom_emoji_id: attrs["emoji-id"].value,
          }
        } else if (tag.name === "tg-time") {
          entity = dateEntity(
            attrs.unix?.value ?? "",
            attrs.format?.value,
            offset,
            0,
            i,
            tag.end - i,
          )
        } else if (tag.name === "span") {
          if (attrs.class?.value !== "tg-spoiler")
            reject(
              "UNSUPPORTED_HTML_ATTRIBUTE",
              "Only class=tg-spoiler is supported",
              i,
              tag.end - i,
            )
          entity = { type: "spoiler", offset, length: 0 }
        } else if (tag.name === "blockquote") {
          if (attrs.expandable && attrs.expandable.value !== "")
            reject(
              "INVALID_HTML_ATTRIBUTE",
              "expandable is a boolean attribute",
              i,
              tag.end - i,
            )
          entity = {
            type: attrs.expandable ? "expandable_blockquote" : "blockquote",
            offset,
            length: 0,
          }
        } else
          entity = {
            type: tagTypes[tag.name],
            offset,
            length: 0,
          } as ParsedMessageEntity
        if (!codeInPre) builder.entities.push(entity)
        stack.push({
          name: tag.name,
          start: i,
          entity,
          codeInPre,
          destination: attrs.href,
        })
      }
      builder.syntax(i, tag.end - i)
      i = tag.end
    }
    if (stack.length)
      reject(
        "UNCLOSED_HTML_TAG",
        `Unclosed ${stack[stack.length - 1].name} tag`,
        stack[stack.length - 1].start,
        source.length - stack[stack.length - 1].start,
      )
    return builder.finish()
  })
}

function escapeHtml(value: string): string {
  return value.replace(
    /[&<>"']/g,
    (char) =>
      ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" })[
        char
      ]!,
  )
}

export function serializeTelegramHtml(
  parsed: ParsedMessage,
): CodecResult<string> {
  const allowed = new Set([
    ...Object.values(tagTypes),
    "expandable_blockquote",
    "mention_by_id",
  ])
  const diagnostics = representability(parsed, allowed)
  if (diagnostics.length) return { ok: false, diagnostics }
  const tags: Record<string, string> = {
    bold: "b",
    italic: "i",
    underline: "u",
    strikethrough: "s",
    spoiler: "tg-spoiler",
    code: "code",
    pre: "pre",
    blockquote: "blockquote",
  }
  function render(from: number, to: number, nodes: EntityNode[]): string {
    let output = ""
    let cursor = from
    for (const { entity, children } of nodes) {
      output += escapeHtml(parsed.text.slice(cursor, entity.offset))
      const inner = render(
        entity.offset,
        entity.offset + entity.length,
        children,
      )
      switch (entity.type) {
        case "text_link":
          output += `<a href="${escapeHtml(entity.url)}">${inner}</a>`
          break
        case "mention_by_id":
          output += `<a href="tg://user?id=${entity.userId}">${inner}</a>`
          break
        case "custom_emoji":
          output += `<tg-emoji emoji-id="${entity.custom_emoji_id}">${inner}</tg-emoji>`
          break
        case "date_time":
          output += `<tg-time unix="${entity.unix_time}"${entity.date_time_format === undefined ? "" : ` format="${escapeHtml(entity.date_time_format)}"`}>${inner}</tg-time>`
          break
        case "expandable_blockquote":
          output += `<blockquote expandable>${inner}</blockquote>`
          break
        case "pre":
          output +=
            entity.language === undefined
              ? `<pre>${inner}</pre>`
              : `<pre><code class="language-${escapeHtml(entity.language)}">${inner}</code></pre>`
          break
        default:
          output += `<${tags[entity.type]}>${inner}</${tags[entity.type]}>`
      }
      cursor = entity.offset + entity.length
    }
    return output + escapeHtml(parsed.text.slice(cursor, to))
  }
  return checkedSerialization(
    parsed,
    render(0, parsed.text.length, entityTree(parsed.entities)),
    parseTelegramHtml,
  )
}
